import rpointInPolygon from 'robust-point-in-polygon';
import {
  BufferGeometry,
  Camera,
  Layers,
  Matrix4,
  Mesh,
  Object3D,
  Vector2,
  Vector2Tuple,
} from 'three';
import { CONTAINED, INTERSECTED, NOT_INTERSECTED } from 'three-mesh-bvh';

import { traverseUntil } from '@spazious/dollhouse-utils';

import '@spazious/dollhouse-three/shims';

const toScreenSpaceMatrix = new Matrix4();
/**
 * Inspired by https://github.com/gkjohnson/three-mesh-bvh/blob/master/example/selection.js
 *
 * A class for selecting objects using a bounding volume hierarchy.
 *
 * Using projection to screen space to determine if a point of a triangle is inside a selection region.
 */
export class BVHSelection {
  selectionMode: 'centroid' | 'centroid-visible' | 'intersection' = 'intersection';
  useBoundsTree: boolean = true;
  selectModel: boolean = true;
  layers = new Layers();
  constructor(public camera: Camera, public object: Object3D) {}

  select(points: Vector2[]) {
    const objects: Object3D[] = [];
    const ptuples: Vector2Tuple[] = points.map((p) => p.toArray());

    traverseUntil(this.object, (node) => {
      if (!node.layers.test(this.layers)) {
        return false;
      }

      const typedNode = node as Mesh<BufferGeometry>;

      if (!typedNode.geometry) {
        return;
      }
      let boundsTree = typedNode.geometry?.boundsTree;

      if (
        !boundsTree &&
        typedNode.geometry.computeBoundsTree &&
        // Empty geometries can't have bounds trees
        typedNode.geometry.attributes?.position?.count
      ) {
        typedNode.geometry.boundsTree = typedNode.geometry.computeBoundsTree({
          setBoundingBox: false,
        });
      }
      if (!boundsTree) {
        return;
      }

      toScreenSpaceMatrix
        .copy(node.matrixWorld)
        .premultiply(this.camera.matrixWorldInverse)
        .premultiply(this.camera.projectionMatrix);

      const intersected = boundsTree.shapecast({
        intersectsBounds: (box) => {
          const { min, max } = box.clone();

          const vertices = [min, max];

          let result = NOT_INTERSECTED;

          for (let i = 0; i < 2; i++) {
            const v = vertices[i];
            v.applyMatrix4(toScreenSpaceMatrix);

            const crossing = rpointInPolygon(ptuples, [v.x, v.y]);

            // If the box is partially contained then the object is intersected
            if (crossing === 1 && result === CONTAINED) {
              return INTERSECTED;
            } else if (crossing < 1) {
              result = CONTAINED;
            }
          }

          return result;
        },

        intersectsTriangle: (tri, _, contained) => {
          if (contained) {
            return true;
          }

          // get the projected vertices
          const vertices = [tri.a, tri.b, tri.c];

          // check if any of the vertices are inside the selection and if so then the triangle is selected
          for (let j = 0; j < 3; j++) {
            const v = vertices[j];
            v.applyMatrix4(toScreenSpaceMatrix);

            const crossing = rpointInPolygon(ptuples, [v.x, v.y]);

            if (crossing < 1) {
              return true;
            }
          }

          return false;
        },
      });

      if (intersected) {
        objects.push(node);
      }
    });

    return objects;
  }
}
